import {
  ApiTypesEnum,
  BasicContactPerson,
  CarrierOrGroupOutDTO,
  CGV2LocationOutDTO,
  DistributionTypeEnum,
  DistributionWithQuotesV4Response,
  ExpeditionDetailV4OutDTO,
  FindCarrierTypeEnum,
  LocationContactTypeEnum,
  NotifyTargetTypeEnum,
  SelectedItemTypeEnum,
} from '@api/ringil-api.v2.tsx';
import { ExpeditionLocality, LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import {
  Carrier,
  CarrierDistributionSelectorData,
  DistributionType,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { PrefillOptions } from '@components/RingilComponents/expeditionPrefillOptions.ts';
import { buildCarrierNoteWithLocationBlock } from '@utils/carrierNoteMerge.ts';
import { generateUUID } from '@utils/utils.tsx';
import dayjs from 'dayjs';

/**
 * Expedition detail → create-shipment form model.
 *
 * The input is deliberately typed structurally rather than as `ExpeditionDetailV4OutDTO`, so the
 * same mapper serves every prefill source: copy-from-expedition, copy-from-template, the shape
 * adapted from a supplier notification, and `MultipleExpeditionDetailV4OutDTO` (which carries the
 * same fields with a slimmer distribution response).
 */
export type DistributionForPrefill = Pick<
  DistributionWithQuotesV4Response,
  'price' | 'currency' | 'isAssignFixedDistribution' | 'distributionType' | 'selectedItems' | 'additionalData' | 'timezoneId' | 'currencyLockType'
>;

export type ExpeditionDetailForPrefill = Pick<
  ExpeditionDetailV4OutDTO,
  | 'id'
  | 'name'
  | 'expeditionType'
  | 'loadingTypes'
  | 'truckType'
  | 'cargoTypeCode'
  | 'adrWeight'
  | 'incoterms'
  | 'transportationType'
  | 'requirementCodes'
  | 'locations'
  | 'carrierNote'
  | 'labels'
  | 'internalInfo'
  | 'ladenFactor'
  | 'allowPartialDelivery'
  | 'supplierNotificationLink'
  | 'polandSent'
  | 'huEkaerNumber'
  | 'roUitNumber'
  | 'utnCode'
> & {
  distributions: DistributionForPrefill[];
  fromSupplierNotification?: boolean;
};

export type DeadlineMode = 'relative' | 'absolute';

export interface DetailToFormContext {
  prefillOptions: PrefillOptions;
  /** Response-deadline offset in hours, from the company's distribution settings. */
  effectiveHours: number;
  defaultCurrencyIsoCode: string;
  defaultTimezoneId: string;
  deadlineMode: DeadlineMode;
  editDistributionAllowed: boolean;
  /** Current address-book data for every `companyLocationId` on the source, when it could be loaded. */
  locationDataByLocationId: Map<string, CGV2LocationOutDTO>;
}

const mapContact = (contact?: BasicContactPerson) => ({
  contactEmail: contact?.contactEmail,
  contactName: contact?.contactName,
  contactPhone: contact?.contactPhone,
});

export const toLocationNoteStops = (locations: ExpeditionLocality[]): { name?: string; locationNote?: string }[] =>
  locations.map(l => ({
    name: l.location?.locationName || l.manualAddress?.name,
    locationNote: l.location?.locationNote,
  }));

const resolveCarrierType = (item: CarrierOrGroupOutDTO): FindCarrierTypeEnum => {
  if (item.selectedItemType === SelectedItemTypeEnum.API) return FindCarrierTypeEnum.CARRIER;
  if (item.selectedItemType === SelectedItemTypeEnum.GROUP) return FindCarrierTypeEnum.GROUP;
  if (item.selectedItemType === SelectedItemTypeEnum.CARRIER && item.userId && item.userId.length > 0) return FindCarrierTypeEnum.CARRIER_USER;
  return FindCarrierTypeEnum.CARRIER;
};

/**
 * A direct-assign distribution only ever carries Dachser codes; spot and fixed distributions can
 * also carry Schenker, DHL and FedEx ones.
 */
const mapSelectedItem = (item: CarrierOrGroupOutDTO, distribution: DistributionForPrefill, withApiProductCodes: boolean): Carrier => ({
  id: item.id,
  // Carried along so anything reporting on this selection can name the carrier instead of its id.
  name: item.name,
  userIds: item.userId ? [item.userId] : null,
  apiTypes: item.apiType ? [item.apiType as unknown as ApiTypesEnum] : null,
  carrierType: resolveCarrierType(item),
  _dachserProductTypeCode: distribution.additionalData?.dachserProductType,
  _dachserDeliveryTermCode: distribution.additionalData?.dachserDeliveryTerm,
  ...(withApiProductCodes
    ? {
      _schenkerProductTypeCode: distribution.additionalData?.schenkerProductType,
      _schenkerAddonsTypeCodes: distribution.additionalData?.schenkerAddonsType,
      _dhlProductTypeCode: distribution.additionalData?.dhlProductType,
      _fedexProductTypeCode: distribution.additionalData?.fedexProductType,
      _fedexPayerType: distribution.additionalData?.fedexPayerType,
      _fedexPayerAccountNumber: distribution.additionalData?.fedexPayerAccountNumber,
      _fedexPayerCountry: distribution.additionalData?.fedexPayerCountry,
      _fedexDeclaredValue: distribution.additionalData?.fedexDeclaredValue,
      _fedexDeclaredValueCurrency: distribution.additionalData?.fedexDeclaredValueCurrency,
      _fedexSignatureOption: distribution.additionalData?.fedexSignatureOption,
      _fedexPackagingType: distribution.additionalData?.fedexPackagingType,
      _fedexBookingConfirmationNumber: distribution.additionalData?.fedexBookingConfirmationNumber,
      _fedexAccountId: distribution.additionalData?.fedexAccountId,
    }
    : {}),
});

const buildDistributionDraft = (
  type: DistributionType,
  distribution: DistributionForPrefill,
  price: CarrierDistributionSelectorData['price'],
  ctx: DetailToFormContext,
): CarrierDistributionSelectorData => ({
  distributionType: type,
  price,
  selectedItems: distribution.selectedItems.map(item => mapSelectedItem(item, distribution, type !== DistributionType.ASSIGN)),
  responseDeadlineMode: ctx.deadlineMode,
  responseDeadlineOffsetValue: ctx.effectiveHours,
  responseDeadlineOffsetUnit: 'hours' as const,
  responseDeadlineLocalTime: ctx.deadlineMode === 'absolute' ? dayjs().add(ctx.effectiveHours, 'hour') : undefined,
  responseDeadlineTimezone: distribution.timezoneId || ctx.defaultTimezoneId,
  notifyAssignedCarrier: false,
  currencyLockType: distribution.currencyLockType,
});

/**
 * Writes `detail` into `draft`. Returns the distribution it pre-filled, which the caller keeps as
 * the "original" the carrier selector diffs against — `undefined` means the carrier section was not
 * pre-filled and any previously remembered original still stands.
 */
export const mapExpeditionDetailToForm = (
  detail: ExpeditionDetailForPrefill,
  draft: NewExpeditionForm,
  ctx: DetailToFormContext,
): CarrierDistributionSelectorData | undefined => {
  const { prefillOptions, locationDataByLocationId } = ctx;

  draft.locations = [];

  if (prefillOptions?.linkToSN && detail.supplierNotificationLink) {
    draft.supplierNotificationLink = detail.supplierNotificationLink;
  }

  // Shipment-level parameters. (In the original these were assigned inside the location loop, once
  // per location, which had the same effect for any shipment with at least one stop.)
  draft.name = detail.name;
  draft.expeditionType = detail.expeditionType;
  draft.loadingTypes = detail.loadingTypes || [];
  draft.truckTypeCode = detail.truckType?.code;
  if (detail.cargoTypeCode) {
    draft.cargoType = {
      cargoTypeCode: detail.cargoTypeCode,
      adrWeight: detail.adrWeight,
    };
  }
  draft.incoterms = detail.incoterms;
  draft.transportationType = detail.transportationType;
  draft.specialRequirements = detail.requirementCodes || [];

  // Country-specific transport registrations. They belong to the shipment rather than to any one
  // stop, so a copy carries them over like the rest of the shipment parameters.
  draft.huEkaerNumber = detail.huEkaerNumber;
  draft.roUitNumber = detail.roUitNumber;
  draft.utnCode = detail.utnCode;
  draft.polandSent = detail.polandSent
    ? {
      sentNumber: detail.polandSent.sentNumber,
      sentCarrierUpdateKey: detail.polandSent.sentCarrierUpdateKey,
    }
    : null;

  detail.locations.forEach((loc, index) => {
    const newLocation: ExpeditionLocality = {
      id: loc.id,
      localityMode: loc.companyLocationId ? LocalityEntryModeEnum.FROM_LOCATIONS : LocalityEntryModeEnum.MANUALLY,
      timeslots: loc.deliverySlot,
      loadingReference: loc.loadingReference,
      orders: null,
      unloadingOrderIds: index > 0 ? [] : null,
      manualAddress: null,
      location: null,
      notifySelectedPerson: false,
      timezoneId: loc.timezoneId,
    };

    if (loc.companyLocationId) {
      const locationData = locationDataByLocationId.get(loc.companyLocationId);
      const allLocationContacts = locationData?.contacts || [];
      newLocation.localityMode = LocalityEntryModeEnum.FROM_LOCATIONS;
      // The loaded entry is the replacement when this stop's location was superseded (the prefill hook
      // resolves the forward), so the id comes from it and not from the source stop — otherwise the form
      // would carry the old id next to the replacement's address and the save would write the old one.
      newLocation.companyLocationId = locationData?.locationId ?? loc.companyLocationId;
      newLocation.timezoneId = locationData?.timezoneId || loc.timezoneId;
      // The prefill hook resolved this stop's superseded location to its replacement.
      const supersededSwap = locationData != null && locationData.locationId !== loc.companyLocationId;
      if (supersededSwap) {
        newLocation.supersededFromLocationName = loc.name;
      }

      // The person stored on the source shipment works at the *old* location. Keep them when the
      // replacement has the same contact (matched by e-mail); otherwise prefill the replacement's own
      // first contact, which is exactly what picking the location by hand does.
      const matchedMainContact = loc.selectedPerson ? allLocationContacts.find(contact => contact.contactEmail === loc.selectedPerson.contactEmail) : undefined;
      const mainContact = supersededSwap ? (matchedMainContact ?? allLocationContacts[0]) : matchedMainContact;

      // The shipment carries its own contact selection (who to notify) — keep it regardless of
      // where the rest of the location data comes from.
      const expeditionContactSelection = {
        defaultContact: mapContact(supersededSwap ? mainContact : loc.selectedPerson),
        contactPersons: (loc.additionalContacts || [])
          .map(additionalContact => allLocationContacts.find(locationContact => locationContact.contactEmail === additionalContact.contactEmail))
          .filter(contact => contact !== undefined),
        enabledEmailNotificationForContactPerson: loc.notifySelectedPerson,
      };

      if (locationData) {
        // Use the current address-book entry (address, company, contacts, type, timezone).
        newLocation.location = {
          ...locationData,
          locationId: locationData.locationId,
          ...expeditionContactSelection,
        };
      } else {
        // Fall back to the snapshot on the source shipment when the location could not be loaded.
        newLocation.location = {
          locationId: loc.companyLocationId,
          locationName: loc.name,
          companyName: loc.companyName,
          addressTemplate: {
            name: loc.name,
            country: loc.country,
            postalCode: loc.postalCode,
            city: loc.city,
            streetNr: loc.streetNr,
          },
          timezoneId: loc.timezoneId,
          contacts: [],
          locationContactType: loc.isPartnerLocation ? LocationContactTypeEnum.PARTNER_LOCATION : LocationContactTypeEnum.OUR_LOCATION,
          isExternal: false,
          isPartnerLocation: loc.isPartnerLocation || false,
          ...expeditionContactSelection,
        };
      }

      if (loc.selectedPerson) {
        newLocation.selectedPerson = mainContact;
      }

      const autoNotifyContacts = allLocationContacts.filter(c => c.automaticPrefillToNotify === true);
      const additionalFromOriginal = loc.additionalContacts || [];

      const allContactsToNotify = [...additionalFromOriginal, ...autoNotifyContacts];
      const uniqueEmailsToNotify = allContactsToNotify.map(contact => contact.contactEmail);
      const uniqueContactsToNotify = allLocationContacts.filter(contact => uniqueEmailsToNotify.includes(contact.contactEmail));

      newLocation.additionalContacts = uniqueContactsToNotify.map(mapContact);
      if (!supersededSwap && !detail.fromSupplierNotification) {
        newLocation.additionalPersons = additionalFromOriginal
          .filter(contact => contact.contactEmail && !allLocationContacts.some(locationContact => locationContact.contactEmail === contact.contactEmail))
          .map(contact => ({ name: contact.contactName || '', email: contact.contactEmail }));
      }

      newLocation.manualAddress = {};
    } else {
      newLocation.localityMode = LocalityEntryModeEnum.MANUALLY;
      newLocation.manualAddress = {
        name: loc.name,
        city: loc.city,
        countryCode3: loc.country,
        postalCode: loc.postalCode,
        streetNr: loc.streetNr,
        mainContactPerson: loc.selectedPerson?.contactName,
        mainContactEmail: loc.selectedPerson?.contactEmail,
        mainContactPhone: loc.selectedPerson?.contactPhone,
        notifySelectedPerson: loc.notifySelectedPerson,
        additionalPersons:
          loc.additionalContacts?.map(contact => ({
            name: contact.contactName,
            email: contact.contactEmail,
          })) || [],
        timezoneId: loc.timezoneId,
      };
    }

    if (prefillOptions.cargoAndOrders) {
      if (loc.loadedOrders && loc.loadedOrders.length > 0) {
        newLocation.orders = loc.loadedOrders.map(order => ({
          id: order.id,
          orderName: order.orderName,
          orderNote: order.orderNote,
          orderValue: order.orderValue,
          orderAmount: order.orderAmount,
          orderDescription: order.orderDescription,
          orderTotalWeight: order.orderTotalWeight,
          items: order.items.map(item => ({
            cargoTemplateCode: item.cargoTemplate.code,
            stackable: item.stackable,
            amount: item.amount,
            height: item.height,
            width: item.width,
            length: item.length,
            weight: item.weight,
            itemNote: item.itemNote,
          })),
        }));
      }

      if (loc.unloadedOrders && loc.unloadedOrders.length > 0) {
        newLocation.unloadingOrderIds = loc.unloadedOrders.map(order => order.id);
      }
    }

    draft.locations.push(newLocation);
  });

  draft.carrierNote = buildCarrierNoteWithLocationBlock(prefillOptions.carrierNote ? detail.carrierNote : '', toLocationNoteStops(draft.locations));

  // The new shipment gets fresh order ids so it never shares them with the source.
  const orderIdMap = new Map<string, string>();

  draft.locations.forEach((loc, locIndex) => {
    // Stop ids too: supplier notifications number their stops per notification, so a merge of
    // several would otherwise hold stops sharing an id — and the route list is keyed by it (RD-3065).
    loc.id = generateUUID();


    if (loc.orders?.length) {
      loc.orders.forEach(order => {
        const oldId = order.id;
        const newId = generateUUID();
        orderIdMap.set(oldId, newId);
        order.id = newId;
      });
    }

    if (locIndex > 0 && loc.unloadingOrderIds?.length) {
      loc.unloadingOrderIds = loc.unloadingOrderIds.map(oldId => orderIdMap.get(oldId) || oldId);
    }
  });

  draft.labels = prefillOptions.labels
    ? detail.labels.map(label => ({
      labelValue: label.labelValue,
      labelColor: label.labelColor,
      labelId: label.labelId,
    }))
    : [];

  if (prefillOptions.internalInfo) {
    draft.person = detail.internalInfo?.responsiblePerson
      ? {
        id: detail.internalInfo.responsiblePerson.userId,
        name: detail.internalInfo.responsiblePerson.userFullName,
        active: true,
      }
      : null;
    draft.notifiedTargets =
      detail.internalInfo?.notifiedPerson?.map(person => ({
        id: person.userId,
        notifyTargetType: NotifyTargetTypeEnum.USER,
      })) || [];
    draft.disponentPerson = detail.internalInfo?.disponentPerson
      ? {
        id: detail.internalInfo.disponentPerson.userId,
        name: detail.internalInfo.disponentPerson.userFullName,
        active: true,
      }
      : null;
    draft.pairingSymbol = detail.internalInfo?.pairingSymbol;
    draft.internalNote = detail.internalInfo?.internalNote;
    draft.rebilling = detail.internalInfo?.rebilling
      ? {
        invoiceRebillingType: detail.internalInfo.rebilling.invoiceRebillingType,
        invoicePartner: detail.internalInfo.rebilling.invoicePartner,
        invoiceDepartment: detail.internalInfo.rebilling.invoiceDepartment,
      }
      : null;
    draft.folders = (detail.internalInfo?.folders ?? []).map(folder => ({
      id: folder.folderId,
      name: folder.folderName,
      active: true,
    }));
    draft.customsDeclarationCompanies = detail.internalInfo?.customsDeclarationCompanies;
    draft.internalInfoAccountCodeListId = detail.internalInfo?.internalInfoAccount?.id;
    draft.internalInfoCostCenterCodeListId = detail.internalInfo?.internalInfoCostCenter?.id;
    draft.internalInfoCostUnitCodeListId = detail.internalInfo?.internalInfoCostUnit?.id;
    draft.internalInfoProjectCodeListId = detail.internalInfo?.internalInfoProject?.id;
  }

  if (!(ctx.editDistributionAllowed && prefillOptions.carrierSelection && detail.distributions?.length > 0)) return undefined;

  // The most recent distribution is the one worth repeating.
  const distribution = detail.distributions[detail.distributions.length - 1];

  const draftPrice =
    distribution.price && distribution.currency
      ? { price: distribution.price, currency: distribution.currency }
      : draft.distribution?.price || { price: 0, currency: ctx.defaultCurrencyIsoCode };

  if (distribution.isAssignFixedDistribution) {
    draft.distribution = buildDistributionDraft(DistributionType.ASSIGN, distribution, draftPrice, ctx);
  } else if (distribution.distributionType === DistributionTypeEnum.FIXED_PRICE) {
    draft.distribution = buildDistributionDraft(DistributionType.FIXED, distribution, draftPrice, ctx);
  } else if (distribution.distributionType === DistributionTypeEnum.SPOT) {
    draft.distribution = {
      ...buildDistributionDraft(DistributionType.SPOT, distribution, draftPrice, ctx),
      ladenFactor: detail.ladenFactor > 0,
      ladenFactorValue: detail.ladenFactor,
      allowPartialDelivery: detail.allowPartialDelivery,
    };
  }

  return draft.distribution;
};